import { AxiosPromise, ResponseType } from 'axios';

import { ApiService } from 'app/services/api.service';
import { OAuthEnum, StorageEnum } from 'app/shared/enums';
import getWindowConfig from 'app/shared/helpers/getWindowConfig';
import { OAuthResultType } from 'app/shared/types/OAuthResult.type';

const axiosConfig = {
  headers: {
    'Content-Type': 'multipart/form-data',
  },
  responseType: 'text' as ResponseType,
};

const getRegisterRedirection = (
  oAuthProvider: OAuthEnum
): AxiosPromise<string> => {
  return ApiService.get(`oauth2/${oAuthProvider}`, axiosConfig);
};

const getLoginRedirection = (oAuthProvider: OAuthEnum) => {
  return ApiService.get(`oauth2/${oAuthProvider}`, axiosConfig);
};

const wasInitialized = (win: Window) =>
  win && !win.location.href.includes('about:');

const openNewWindow = (): Window | null => {
  const win = window.open('', '', getWindowConfig());

  if (!win) {
    return null;
  }

  const reopen = (window: Window) => {
    window.close();
    return openNewWindow();
  };

  try {
    if (wasInitialized(win)) {
      return reopen(win);
    }
  } catch (e) {
    return reopen(win);
  }

  win.focus();
  return win;
};

const handleRedirection = (win: Window) =>
  new Promise<void>((resolve, reject) => {
    const cleanup = () => {
      if (checkIsPopupClosedByUserInterval) {
        clearInterval(checkIsPopupClosedByUserInterval);
      }

      channel.close();
      window.removeEventListener('storage', storageHandler);
    };

    // BroadcastChannel handler
    const channel = new BroadcastChannel(StorageEnum.Oauth2Result);

    channel.onmessage = (e) => {
      cleanup();
      e.data.success ? resolve() : reject();
    };

    // LocalStorage fallback handler
    const storageHandler = (e: StorageEvent) => {
      if (e.key === StorageEnum.Oauth2Result && e.newValue) {
        const data = JSON.parse(e.newValue) as OAuthResultType;

        localStorage.removeItem(StorageEnum.Oauth2Result);
        cleanup();
        data.success ? resolve() : reject();
      }
    };

    window.addEventListener('storage', storageHandler);

    const checkIsPopupClosedByUserInterval = window.setInterval(() => {
      if (win.closed) {
        cleanup();
        reject(new Error('OAuth popup closed by user'));
      }
    }, 1000);
  });

export const registerOAuth = (oAuthProvider: OAuthEnum) => {
  const win = openNewWindow();

  if (!win) {
    return Promise.reject(new Error('OAuth popup blocked'));
  }

  getRegisterRedirection(oAuthProvider).then(({ data: responseUrl }) => {
    win.location.replace(responseUrl);
  });

  return handleRedirection(win);
};

export const loginOAuth = (oAuthProvider: OAuthEnum) => {
  const win = openNewWindow();

  if (!win) {
    return Promise.reject(new Error('OAuth popup blocked'));
  }

  getLoginRedirection(oAuthProvider).then(({ data: responseUrl }) =>
    win.location.replace(responseUrl)
  );

  return handleRedirection(win);
};
